import {
  SettingHeader,
  SettingsContainer,
  SettingsContent
} from '@components/ui/settings';
import useProcess from '@hooks/useProcess';
import useStores from '@hooks/useStore';
import { Plus } from 'lucide-react';
import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ParamsList } from '@/routes/routes.types';
import { CreateWorkspacePanel } from './components/CreateWorkspacePanel';
import { DeleteWorkspaceDialog } from './components/DeleteWorkspaceDialog';
import { EmptyWorkspacesState } from './components/EmptyWorkspacesState';
import { WorkspaceList } from './components/WorkspaceList';
import { WorkspaceListSkeleton } from './components/WorkspaceListSkeleton';
import { useWorkspaceActions } from './hooks/useWorkspaceActions';

export const WorkspacesPage = observer(() => {
  const { workspaceStore } = useStores();
  const process = useProcess();
  const processId = useParams()[ParamsList.ProcessId];

  useEffect(() => {
    if (processId) {
      workspaceStore.fetchWorkspaces(processId);
    }
  }, [processId, workspaceStore]);

  const processWorkflows = process?.workflows ?? [];
  const workflowNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const wf of processWorkflows) {
      names[wf.id] = wf.name;
    }
    return names;
  }, [processWorkflows]);

  const {
    showCreateForm,
    setShowCreateForm,
    editingId,
    setEditingId,
    deletingWorkspace,
    setDeletingWorkspace,
    handleCreate,
    handleUpdate,
    handleDelete
  } = useWorkspaceActions(processId ?? '');

  if (!processId) {
    return null;
  }

  const workspaces = workspaceStore.getWorkspacesForProcess(processId);
  const loading = workspaceStore.isLoading(processId);

  return (
    <SettingsContainer>
      <SettingHeader
        description={`Manage workspaces for ${process?.name ?? 'this process'}. Each workspace generates its own set of Trace workflows with an optional version.`}
        title="Workspaces"
      />
      <SettingsContent>
        <div className="flex flex-col gap-4">
          {!loading && workspaces.length > 0 && (
            <div className="flex justify-end">
              <Button
                disabled={showCreateForm}
                onClick={() => setShowCreateForm(true)}
                size="sm"
                variant="default"
              >
                <Plus className="mr-2 h-4 w-4" />
                New workspace
              </Button>
            </div>
          )}

          <CreateWorkspacePanel
            onCancel={() => setShowCreateForm(false)}
            onSubmit={handleCreate}
            processId={processId}
            visible={showCreateForm}
            workspaceCount={workspaces.length}
          />

          {loading && <WorkspaceListSkeleton />}

          {!loading && workspaces.length === 0 && !showCreateForm && (
            <EmptyWorkspacesState
              disabled={showCreateForm}
              onCreateClick={() => setShowCreateForm(true)}
            />
          )}

          {!loading && (workspaces.length > 0 || showCreateForm) && (
            <WorkspaceList
              editingId={editingId}
              onCancelEdit={() => setEditingId(null)}
              onRequestDelete={setDeletingWorkspace}
              onToggleEdit={id => setEditingId(id || null)}
              onUpdate={handleUpdate}
              processId={processId}
              workspaces={workspaces}
            />
          )}
        </div>
      </SettingsContent>

      <DeleteWorkspaceDialog
        onClose={() => setDeletingWorkspace(null)}
        onConfirm={handleDelete}
        workflowNames={workflowNames}
        workspace={deletingWorkspace}
      />
    </SettingsContainer>
  );
});
